import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { DateRange } from 'react-date-range';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

import ButtonMC from './ButtonMC';
import IconMC from './IconMC';

/*
## Props object for DateRangeMC

label: optional
  - String that defines the label for the date range component.

name: required
  - String that defines the name for the date range component.

defaultDate: optional
  - Object that defines the default date range for the component. It must have the following structure: { startDate: Date, endDate: Date }.

handleChange: required
  - Function that handles the change event of the date range component and receives an object with the following structure: { value: { startDate: Date, endDate: Date } | null, name: String }.

className: optional
  - String that defines the class name for the date range trigger.

useFloatingLabel: optional
  - When true, renders the label inside the trigger (Mercately design system standard).

dateFormat: optional
  - Moment.js format for displaying the selected range. Default: 'DD/MM/YYYY'.

maxDate: optional
  - Maximum selectable date passed to react-date-range.

*/

const DateRangeMC = ({
  label = '',
  name,
  defaultDate,
  handleChange,
  className = '',
  useFloatingLabel = false,
  dateFormat = 'DD/MM/YYYY',
  maxDate
}) => {
  const { t } = useTranslation();
  const dateRangeRef = useRef(null);
  const [pickerDate, setPickerDate] = useState(defaultDate || null);
  const [open, setOpen] = useState(false);
  const [hasSpaceBelow, setHasSpaceBelow] = useState(true);
  const inputRef = useRef(null);
  const lastDefaultDateRef = useRef(null);

  const verifySpaceBelow = () => {
    if (!inputRef.current) return;
    const inputRect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const calendarHeight = 350;
    setHasSpaceBelow(spaceBelow >= calendarHeight);
  };

  const openPicker = () => {
    verifySpaceBelow();
    setOpen(true);
  };

  const closePicker = () => {
    setOpen(false);
  };

  const togglePicker = () => {
    if (open) {
      closePicker();
      return;
    }
    openPicker();
  };

  const cancelAction = () => {
    setPickerDate(defaultDate || null);
    closePicker();
  };

  const successAction = () => {
    handleChange({ value: pickerDate, name });
    closePicker();
  };

  const removeDate = (event) => {
    event.stopPropagation();
    setPickerDate(null);
    handleChange({
      value: null,
      name
    });
  };

  const hasDate = pickerDate && pickerDate.startDate && pickerDate.endDate;

  const pickerLabel = hasDate
    ? `${moment(pickerDate.startDate).format(dateFormat)} - ${moment(pickerDate.endDate).format(dateFormat)}`
    : t('customer.view_list.modal_filters.select_date');

  const handleClickOutside = (event) => {
    if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) {
      setPickerDate(defaultDate || null);
      closePicker();
    }
  };

  useEffect(() => {
    if (
      defaultDate
      && (
        defaultDate.startDate !== lastDefaultDateRef.current?.startDate
        || defaultDate.endDate !== lastDefaultDateRef.current?.endDate
      )
    ) {
      lastDefaultDateRef.current = defaultDate;
      setPickerDate(defaultDate);
    } else if (!defaultDate && lastDefaultDateRef.current) {
      lastDefaultDateRef.current = null;
      setPickerDate(null);
    }

    verifySpaceBelow();
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [defaultDate]);

  const inputContainerClass = [
    'tw-w-full',
    'tw-flex',
    'tw-text-default',
    'tw-pl-4',
    'tw-items-center',
    'tw-flex-wrap',
    'tw-justify-between',
    'tw-bg-gray-50',
    'tw-rounded-xl',
    'tw-border',
    'tw-border-gray-200',
    'tw-cursor-pointer',
    useFloatingLabel ? 'tw-relative tw-min-h-[42px] tw-pt-5 tw-pb-2' : 'tw-py-2.5 tw-min-h-12'
  ].join(' ');

  const floatingContainerClass = [
    'tw-bg-white',
    'tw-absolute',
    hasSpaceBelow ? 'tw-top-12' : 'tw-top-[-360px]',
    'tw-left-0',
    'tw-rounded-xl',
    'tw-z-10',
    'tw-border',
    'tw-border-gray-400',
    'tw-overflow-hidden',
    'tw-flex',
    'tw-flex-col',
    'tw-min-w-[300px]',
    'tw-shadow-lg'
  ].join(' ');

  const valueClassName = hasDate ? 'tw-text-sm tw-text-gray-800' : 'tw-text-sm tw-text-gray-400';

  return (
    <div className="tw-wrapper" ref={dateRangeRef}>
      {label && !useFloatingLabel && (
        <div className="tw-text-sm tw-font-bold tw-mb-1">{label}</div>
      )}
      <div className="tw-relative tw-text-sm">
        <div
          className={`${inputContainerClass} ${className}`}
          onClick={togglePicker}
          ref={inputRef}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              togglePicker();
            }
          }}
        >
          {useFloatingLabel && label && (
            <label className="tw-absolute tw-text-xs tw-text-blue-500 tw-top-[5px] tw-z-10 tw-left-4 tw-pointer-events-none">
              {label}
            </label>
          )}
          <span className={valueClassName}>{pickerLabel}</span>
          <div className="tw-flex tw-py-1 tw-pr-4 tw-items-center tw-gap-1 tw-text-gray-600" aria-hidden="true">
            {hasDate && (
              <IconMC
                name="close-outline"
                size="base"
                onClick={removeDate}
              />
            )}
            <IconMC
              name="calendar-outline"
              size="base"
            />
          </div>
        </div>
        {open && (
          <div
            className={floatingContainerClass}
            id={`date-range-${name}`}
          >
            <DateRange
              locale={es}
              className="date-picker-overwrite tw-placeholder-gray-400"
              ranges={[
                {
                  startDate: pickerDate?.startDate || new Date(),
                  endDate: pickerDate?.endDate || new Date(),
                  key: 'selection'
                }
              ]}
              onChange={({ selection }) => setPickerDate({
                startDate: selection.startDate,
                endDate: selection.endDate
              })}
              showDateDisplay={false}
              moveRangeOnFirstSelection={false}
              showPreview={false}
              maxDate={maxDate}
            />
            <ButtonMC
              text={t('customer.view_list.modal_filters.cancel_date')}
              variant="DANGER"
              onClick={cancelAction}
              className="tw-border-0"
              outlined
            />
            <ButtonMC
              text={t('customer.view_list.modal_filters.apply_date')}
              variant="SECONDARY"
              onClick={successAction}
              disabled={!pickerDate}
              className="tw-border-0"
              outlined
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeMC;
